import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type SignedAmountOperator = '+' | '-';

@Component({
    selector: 'app-signed-amount-input',
    templateUrl: './signed-amount-input.component.html',
    styleUrls: ['./signed-amount-input.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SignedAmountInputComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SignedAmountInputComponent implements ControlValueAccessor, OnChanges {
  operator: SignedAmountOperator = '+';
  displayValue: number | null = null;
  isOperatorDisabled = false;
  isInputDisabled = false;

  @Input() allowDecimals = false;
  @Input() maxDecimalPlaces = 4;
  @Input() inputClass = 'form-control';
  @Input() fixedOperator?: SignedAmountOperator;
  @Input() showOperatorButton = true;

  @Input()
  set operatorDisabled(value: boolean) {
    this.isOperatorDisabled = value === true || value === ('' as any);
  }

  @Input()
  set inputDisabled(value: boolean) {
    this.isInputDisabled = value === true || value === ('' as any);
  }

  @Input()
  set disabled(value: boolean) {
    const isDisabled = value === true || value === ('' as any);
    this.isOperatorDisabled = isDisabled;
    this.isInputDisabled = isDisabled;
  }

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fixedOperator && this.isOperatorDisabled && this.fixedOperator) {
      this.applyFixedOperator();
      this.emitValue();
    }
  }

  writeValue(value: number | null): void {
    if (value === null || value === undefined) {
      this.operator = this.resolveOperator('+');
      this.displayValue = null;
      return;
    }

    const numericValue = Number(value);
    if (isNaN(numericValue)) {
      this.operator = this.resolveOperator('+');
      this.displayValue = null;
      return;
    }

    if (this.isOperatorDisabled && this.fixedOperator) {
      this.operator = this.fixedOperator;
      this.displayValue = Math.abs(numericValue);
      return;
    }

    if (numericValue < 0) {
      this.operator = '-';
      this.displayValue = Math.abs(numericValue);
      return;
    }

    this.operator = '+';
    this.displayValue = numericValue;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isOperatorDisabled = isDisabled;
    this.isInputDisabled = isDisabled;
  }

  toggleOperator(): void {
    if (this.isOperatorDisabled) {
      return;
    }

    this.operator = this.operator === '+' ? '-' : '+';
    this.emitValue();
  }

  onAmountChange(value: number | null): void {
    this.displayValue = value === null || value === undefined ? null : Math.abs(value);
    this.emitValue();
  }

  onBlur(): void {
    this.onTouched();
  }

  private applyFixedOperator(): void {
    if (this.fixedOperator) {
      this.operator = this.fixedOperator;
    }
  }

  private resolveOperator(fallback: SignedAmountOperator): SignedAmountOperator {
    if (this.isOperatorDisabled && this.fixedOperator) {
      return this.fixedOperator;
    }

    return fallback;
  }

  private emitValue(): void {
    if (this.isOperatorDisabled && this.fixedOperator) {
      this.operator = this.fixedOperator;
    }

    if (this.displayValue === null || this.displayValue === undefined) {
      this.onChange(null);
      return;
    }

    const absoluteValue = Math.abs(this.displayValue);
    const signedValue = this.operator === '-' ? -absoluteValue : absoluteValue;
    this.onChange(signedValue);
  }
}
